type Disposer = () => void;

export interface BreakoutOperation {
  readonly session: number;
  readonly handlers: number;
}

/** Owns subscriptions, countdown work, and reset-sensitive async generations. */
export class BreakoutPanelOwner {
  private cleanups: Disposer[] = [];
  private tickTimer: number | null = null;
  private sessionGeneration = 1;
  private handlerGeneration = 0;
  private catalogGeneration = 0;
  private disposed = false;

  replaceHandlers(): void {
    this.stopTick();
    this.handlerGeneration += 1;
    this.catalogGeneration += 1;
  }

  captureOperation(): BreakoutOperation {
    return { session: this.sessionGeneration, handlers: this.handlerGeneration };
  }

  isCurrent(operation: BreakoutOperation): boolean {
    return (
      !this.disposed &&
      operation.session === this.sessionGeneration &&
      operation.handlers === this.handlerGeneration
    );
  }

  beginCatalogLoad(): number {
    return ++this.catalogGeneration;
  }

  isCatalogCurrent(operation: BreakoutOperation, catalogGeneration: number): boolean {
    return this.isCurrent(operation) && catalogGeneration === this.catalogGeneration;
  }

  addCleanup(cleanup: Disposer): void {
    this.cleanups.push(cleanup);
  }

  startTick(callback: () => void): void {
    if (this.tickTimer !== null) return;
    const operation = this.captureOperation();
    this.tickTimer = window.setInterval(() => {
      if (this.isCurrent(operation)) callback();
    }, 1000);
  }

  stopTick(): void {
    if (this.tickTimer === null) return;
    window.clearInterval(this.tickTimer);
    this.tickTimer = null;
  }

  resetSession(): void {
    this.sessionGeneration += 1;
    this.catalogGeneration += 1;
    this.stopTick();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.resetSession();
    for (const cleanup of this.cleanups.splice(0)) cleanup();
  }
}
